import { consentCookieHelper } from '@sainsburys-tech/boltui-utils'
import featureBrandConfigs from '../providers/BrandConfigContext/config/featureBrandConfigs'
import { pushEventAndTrack } from './taggingSerializer'

const supportedCategories = [
  'dishwashers',
  'freezers',
  'fridges',
  'fridge freezers',
  'integrated dishwashers',
  'integrated freezers',
  'integrated fridges',
  'integrated fridge freezers',
  'integrated tumble dryers',
  'integrated washer dryers',
  'integrated washing machines',
  'mini fridges',
  'tumble dryers',
  'washer dryers',
  'washing machines'
]

/**
 * Checks if the product displayed has its category being part of the supported categories for this feature
 * @param breadcrumb {array}  the product category hierarchy list
 * @returns {boolean}
 */
const isSupportedCategory = (breadcrumb = []) => {
  if (breadcrumb.length === 0) return false

  const category = breadcrumb[breadcrumb.length - 1]
  return (
    category &&
    supportedCategories.some(supportedCategory => supportedCategory.toLowerCase() === category.name.toLowerCase())
  )
}

/**
 * Checks if the user has given full consent or specific marketing consent for cookies to be used
 * Cookie values could be for example:
 * - 'consent:true|ts:1671024166026'
 * - ''c1:1|c2:0|c3:1|c4:0|ts:1675081248679|consent:true''
 * @param cookies {object} Cookie object from 'universal-cookie'
 * @returns {boolean} true for full or specific marketing consent
 */
const hasMarketingCookie = (cookies = {}) => {
  const { cookieName, getCategorySection, hasOptedIn } = consentCookieHelper

  if (Object.keys(cookies).length === 0) return false

  const cookieValue = cookies.get(cookieName)
  if (!cookieValue || !cookieValue.includes('consent:')) {
    return false
  }

  const categorySection = getCategorySection(cookieValue, 'marketing')
  if (!categorySection.length && cookieValue.includes('consent:true')) {
    return true
  }

  return hasOptedIn(categorySection[0])
}

const hasMarketingCookieClient = (cookies = '') => {
  const consentCategory = 'marketing'
  const { categories, getConsentCookieValue, getCategorySection, hasOptedIn } = consentCookieHelper

  if (!cookies) return false

  const consentCookieValue = getConsentCookieValue(cookies)
  if (!consentCookieValue || !consentCookieValue.includes('consent:') || !categories[consentCategory]) {
    return false
  }

  const categorySection = getCategorySection(consentCookieValue, consentCategory)
  if (!categorySection.length && consentCookieValue.includes('consent:true')) {
    return true
  }

  return hasOptedIn(categorySection[0])
}

/**
 * Checks if the feature is available for the brand
 * @param brand {string} the current brand
 * @returns {boolean} true if the feature is supported by the brand
 */
const isSupportedBrand = brand => {
  if (!brand) return false
  return featureBrandConfigs[brand.toLowerCase()]?.ENERGY_SAVING
}

const setClientSideYourekoEnabledFlag = (isYourekoEnabled = false) => {
  return `window.__isYourekoEnabled=${isYourekoEnabled}`
}

const getClientSideYourekoEnabledFlag = () => {
  return window?.__isYourekoEnabled || false
}

// Only checking valid brand and supported categories
const isYourRekoValid = (BRAND = '', breadcrumb = []) => isSupportedBrand(BRAND) && isSupportedCategory(breadcrumb)

const TRACKING_EVENTS = {
  IN_VIEW: 'in-view' /* Energy Saving button is viewed (gets displayed on the page) - This is not a Youreko event */,
  VIEWS: 'views' /* User opens the Energy Savings Tool */,
  TRADE_UP_PRODUCT: 'alternative-product-clicks' /* User clicks on a recommended alternative product */,
  CUSTOM_USAGE: 'custom-usage-setting' /* User customises their usage */,
  CUSTOM_YEARS: 'custom-energy-cost-years-setting' /* User customises the lifespan years */,
  CUSTOM_TARIFF: 'custom-tariff-setting' /* User customises their tariff */
}

// Function used by the Youreko script to pass Event names when they trigger inside the Youreko component
const raiseYourekoEvent = eventName => {
  switch (eventName) {
    case TRACKING_EVENTS.IN_VIEW:
      pushEventAndTrack({
        eventInfo: {
          eventName: 'pdp_energySavingButtonViewed',
          eventAction: 'PDP Energy Saving Button Viewed'
        }
      })
      break

    case TRACKING_EVENTS.VIEWS:
      pushEventAndTrack({
        eventInfo: {
          eventName: 'pdp_energySavingButtonInteraction',
          eventAction: 'PDP Energy Saving Button Interaction'
        }
      })
      break

    case TRACKING_EVENTS.TRADE_UP_PRODUCT:
      pushEventAndTrack({
        eventInfo: {
          eventName: 'pdp_energySavingAlternativeProductClicked',
          eventAction: 'PDP Energy Saving Alternative Product Clicked'
        }
      })
      break

    case TRACKING_EVENTS.CUSTOM_USAGE:
      pushEventAndTrack({
        eventInfo: {
          eventName: 'pdp_energySavingUsageOption',
          eventAction: 'PDP Energy Saving Usage Option'
        }
      })
      break

    case TRACKING_EVENTS.CUSTOM_YEARS:
      pushEventAndTrack({
        eventInfo: {
          eventName: 'pdp_energySavingYearsOption',
          eventAction: 'PDP Energy Saving Years Option'
        }
      })
      break

    case TRACKING_EVENTS.CUSTOM_TARIFF:
      pushEventAndTrack({
        eventInfo: {
          eventName: 'pdp_energySavingTariffOption',
          eventAction: 'PDP Energy Saving Tariff Option'
        }
      })
      break

    default:
  }
}

// Attaching the Youreko tracking function to the global scope
const enableGlobalYourekoTracking = () => {
  window.raiseYourekoEvent = raiseYourekoEvent
}

export {
  isYourRekoValid,
  isSupportedCategory,
  hasMarketingCookie,
  hasMarketingCookieClient,
  isSupportedBrand,
  setClientSideYourekoEnabledFlag,
  getClientSideYourekoEnabledFlag,
  TRACKING_EVENTS,
  raiseYourekoEvent,
  enableGlobalYourekoTracking
}
